import { useEffect, useMemo, useState } from 'react';
import MembershipMemberSearchBar from './MembershipMemberSearchBar';
import MembershipMemberTable from './MembershipMemberTable';
import { useMembershipMembersByType } from '../queries';

type MembershipTypeMembersTabProps = {
  typeUuid?: string;
  typeOptions?: Array<{ value: string; label: string }>;
  onSelectMember?: (membershipUuid: string) => void;
  onRevokeMember?: (membershipUuid: string) => void;
  onRenewMember?: (membershipUuid: string, expiresAt?: string) => void;
};

const MembershipTypeMembersTab = ({
  typeUuid,
  typeOptions = [],
  onSelectMember
}: MembershipTypeMembersTabProps) => {
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [selectedTypeUuid, setSelectedTypeUuid] = useState(typeUuid || '');
  const [sortKey, setSortKey] = useState<'name' | 'points' | 'lastVisit' | 'expiry'>('lastVisit');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const resolvedTypeUuid = typeUuid || selectedTypeUuid;

  useEffect(() => {
    if (typeUuid) {
      setSelectedTypeUuid(typeUuid);
    }
  }, [typeUuid]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(0);
      setQuery(queryInput.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [queryInput]);

  const membersQuery = useMembershipMembersByType({
    typeUuid: resolvedTypeUuid || undefined,
    query,
    page,
    pageSize: 20,
    enabled: true
  });

  const rows = useMemo(() => {
    const items = [...(membersQuery.data?.items || [])];
    items.sort((left, right) => {
      const direction = sortDirection === 'asc' ? 1 : -1;
      if (sortKey === 'name') {
        return direction * String(left.userName || left.phone).localeCompare(String(right.userName || right.phone));
      }
      if (sortKey === 'points') {
        return direction * ((Number(left.pointsBalance || 0)) - (Number(right.pointsBalance || 0)));
      }
      if (sortKey === 'expiry') {
        const leftDate = Date.parse(left.expiresAt || '') || 0;
        const rightDate = Date.parse(right.expiresAt || '') || 0;
        return direction * (leftDate - rightDate);
      }
      const leftDate = Date.parse(left.lastVisitedAt || left.issuedAt || left.startsAt || '') || 0;
      const rightDate = Date.parse(right.lastVisitedAt || right.issuedAt || right.startsAt || '') || 0;
      return direction * (leftDate - rightDate);
    });
    return items;
  }, [membersQuery.data?.items, sortDirection, sortKey]);

  const errorMessage = membersQuery.isError ? 'Unable to load members right now.' : '';

  return (
    <div className="membership-tab-content">
      <MembershipMemberSearchBar
        query={queryInput}
        typeUuid={resolvedTypeUuid}
        typeOptions={typeOptions}
        onQueryChange={setQueryInput}
        onTypeChange={(nextTypeUuid) => {
          setPage(0);
          setSelectedTypeUuid(nextTypeUuid);
        }}
      />
      <MembershipMemberTable
        rows={rows}
        loading={membersQuery.isLoading}
        error={errorMessage}
        page={page}
        hasNext={Boolean(membersQuery.data?.hasNext)}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSortChange={(nextKey) => {
          setPage(0);
          setSortDirection((currentDirection) => (
            nextKey === sortKey
              ? (currentDirection === 'asc' ? 'desc' : 'asc')
              : ((nextKey === 'lastVisit' || nextKey === 'expiry' || nextKey === 'points') ? 'desc' : 'asc')
          ));
          setSortKey(nextKey);
        }}
        onPrev={() => setPage((prev) => Math.max(prev - 1, 0))}
        onNext={() => setPage((prev) => prev + 1)}
        onSelectMember={(row) => onSelectMember?.(row.membershipUuid)}
      />
    </div>
  );
};

export default MembershipTypeMembersTab;
